/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import {
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  MenuItem,
  Checkbox,
  ListItemText,
  SxProps,
  Theme,
  useTheme,
  SelectChangeEvent,
  alpha,
  Typography,
  Badge,
  Stack,
  Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useState } from 'react';

interface Option {
  label: string;
  value: string;
  icon?: React.ReactNode;
}

interface OptionSection {
  title: string;
  options: Option[];
}

// Base interface with common properties
interface BaseOptionsDropdownSelectorProps {
  label: string; // Label for the input field
  sx?: SxProps<Theme>; // Optional styles for the FormControl
  value: string | string[]; // Selected value(s)
  isMultiple?: boolean;
  onChange: (event: SelectChangeEvent<string | string[]>) => void; // Event handler for value(s) change
  viewOnly?: boolean; // View-only mode
  isEditable?: boolean; // Whether the dropdown options can be edited
  editLabel?: string; // Label for the edit button
  onEdit?: () => void; // Event handler for edit button click
  maxHeight?: string; // Maximum height of the dropdown menu
}

// Simple options mode
interface SimpleOptionsProps extends BaseOptionsDropdownSelectorProps {
  options: Option[]; // List of options (for simple mode)
  sections?: never; // Explicitly exclude sections
}

// Sectioned options mode
interface SectionedOptionsProps extends BaseOptionsDropdownSelectorProps {
  options?: never; // Explicitly exclude options
  sections: OptionSection[]; // List of sections with options (for sectioned mode)
}

// Union type that ensures only one mode can be used
type OptionsDropdownSelectorProps = SimpleOptionsProps | SectionedOptionsProps;

const OptionsDropdownSelector = ({
  label,
  sx,
  value,
  isMultiple,
  options,
  sections,
  onChange,
  viewOnly = false, // default is false for normal interaction
  isEditable = false,
  onEdit,
  editLabel,
  maxHeight = '200px',
}: OptionsDropdownSelectorProps) => {
  const theme = useTheme();

  const [isOpen, setIsOpen] = useState<boolean>(false);

  // Runtime assertion to ensure only one mode is used
  if (options && sections) {
    throw new Error(
      'OptionsDropdownSelector: Cannot provide both options and sections. Use either options for simple mode or sections for sectioned mode.',
    );
  }

  if (!options && !sections) {
    throw new Error(
      'OptionsDropdownSelector: Must provide either options or sections.',
    );
  }

  // Get all options from either simple options or sections
  const allOptions =
    sections && sections.length > 0
      ? sections.flatMap((section) => section.options)
      : options || [];

  // Check if there's an active selection
  const hasActiveSelection = () => {
    return isMultiple
      ? Array.isArray(value) && value.length > 0
      : value !== '' && value != null;
  };

  // Create a display for selected values in view-only mode
  const renderSelectedValues = () => {
    if (isMultiple) {
      return (value as string[])
        .map((v) => allOptions.find((o) => o.value === v)?.label)
        .filter(Boolean)
        .join(', ');
    }
    return allOptions.find((o) => o.value === value)?.label || '';
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleMenuItemClick = (optionValue: string) => {
    // Create a synthetic event to trigger onChange
    const syntheticEvent = {
      target: {
        value: isMultiple
          ? (value as string[]).includes(optionValue)
            ? (value as string[]).filter((v) => v !== optionValue)
            : [...(value as string[]), optionValue]
          : optionValue,
        name: `${label}-select`,
      },
    } as SelectChangeEvent<string | string[]>;

    onChange(syntheticEvent);

    if (!isMultiple) {
      handleClose();
    }
  };

  const renderMenuItem = (option: Option) => (
    <MenuItem
      key={option.value}
      value={option.value}
      onClick={() => handleMenuItemClick(option.value)}
      sx={{
        padding: '8px',
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.1), // Subtle hover effect
        },
        '&.Mui-selected': {
          backgroundColor: alpha(theme.palette.primary.main, 0.2), // More visible selected state
          color: theme.palette.primary.main,
          fontWeight: 500,
        },
        '&.Mui-selected:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.3), // Darker on hover when selected
        },
        width: '100%',
        maxWidth: '100%',
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        overflow: 'hidden',
      }}
    >
      {isMultiple && (
        <Checkbox
          checked={(value as string[]).indexOf(option.value) > -1}
          style={{
            padding: '0 8px 0 0',
            flexShrink: 0,
          }}
        />
      )}

      {option.icon ? (
        <Stack
          sx={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 2,
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden',
          }}
        >
          {option.icon}
          <Typography
            sx={{
              fontSize: '12px',
              color: (
                isMultiple
                  ? (value as string[]).includes(option.value)
                  : value === option.value
              )
                ? theme.palette.primary.main
                : 'inherit',
            }}
          >
            {option.label}
          </Typography>
        </Stack>
      ) : (
        <ListItemText
          primary={option.label}
          primaryTypographyProps={{
            fontSize: '12px',
            color: (
              isMultiple
                ? (value as string[]).includes(option.value)
                : value === option.value
            )
              ? theme.palette.primary.main
              : 'inherit',
            whiteSpace: 'normal',
            overflow: 'hidden',
          }}
          sx={{
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden',
          }}
        />
      )}
    </MenuItem>
  );

  const renderMenuItems = () => {
    if (sections && sections.length > 0) {
      return sections.map((section) => (
        <div key={section.title}>
          {/* Section title */}
          <MenuItem
            disabled
            sx={{
              padding: '8px 16px',
              opacity: 1,
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
              borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              '&.Mui-disabled': {
                opacity: 1,
              },
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: theme.palette.primary.main,
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
                fontSize: '11px',
              }}
            >
              {section.title}
            </Typography>
          </MenuItem>

          {/* Section options */}
          {section.options.map((option) => renderMenuItem(option))}
        </div>
      ));
    }

    // Simple mode - render options directly
    return options?.map((option) => renderMenuItem(option)) || [];
  };

  const borderStyle = `1px solid ${alpha(theme.palette.primary.main, 0.5)}`;

  return (
    <FormControl
      sx={{
        maxWidth: 195,
        width: '100%',
        position: 'relative',
        ...sx, // Allow overriding styles
      }}
      size="small"
      variant="outlined"
    >
      {/* Badge positioned absolutely inside FormControl */}
      <Badge
        variant="dot"
        invisible={!hasActiveSelection()}
        sx={{
          position: 'absolute',
          top: 0,
          right: 1,
          zIndex: 1,
          '& .MuiBadge-dot': {
            backgroundColor: theme.palette.primary.main,
            width: 8,
            height: 8,
            borderRadius: '50%',
          },
        }}
      />

      {viewOnly ? (
        // In view-only mode, display the label above the field
        <Typography
          sx={{
            fontSize: 12,
            color: theme.palette.text.secondary,
            marginBottom: '8px',
          }}
        >
          {label}
        </Typography>
      ) : (
        // Show InputLabel in editable mode
        <InputLabel
          id={`${label}-select-label`}
          shrink={Boolean(isOpen || (value && value.length > 0))}
          sx={{
            fontSize: 12, // Reduce font size
            color: theme.palette.primary.main,
            transition: '0.2s ease', // Smooth transition for the label
            '&:hover': {
              color: theme.palette.primary.dark,
            },
          }}
        >
          {label}
        </InputLabel>
      )}

      {viewOnly ? (
        // View-only mode: Display selected values as text
        <Typography
          sx={{
            height: 32,
            padding: '0 10px',
            fontSize: 12,
            display: 'flex',
            alignItems: 'center',
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            borderRadius: 2,
            border: `1px solid ${theme.palette.primary.main}`,
          }}
        >
          {renderSelectedValues() || 'No selection'}
        </Typography>
      ) : (
        // Editable mode: Show dropdown select
        <Select
          open={isOpen}
          onOpen={handleOpen}
          onClose={handleClose}
          labelId={`${label}-select-label`}
          id={`${label}-select`}
          multiple={isMultiple}
          value={value}
          // Selection is handled by MenuItem onClick → handleMenuItemClick,
          // so we disable the native Select onChange to prevent double-firing.
          onChange={() => {}}
          input={
            <OutlinedInput
              label={label}
              notched={Boolean(isOpen || (value && value.length > 0))}
              sx={{
                height: 32,
                padding: '0 10px',
                fontSize: 12,
                svg: {
                  color: theme.palette.primary.main,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
                width: '100%',
              }}
            />
          }
          renderValue={(selected) =>
            isMultiple
              ? (selected as string[])
                  .map((v) => allOptions.find((o) => o.value === v)?.label)
                  .filter(Boolean)
                  .join(', ')
              : allOptions.find((o) => o.value === selected)?.label || selected
          }
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: maxHeight,
                maxWidth: '100%',
                borderRight: borderStyle,
                borderBottom: borderStyle,
                borderLeft: borderStyle,
              },
            },
          }}
        >
          {allOptions.length > 0 ? (
            renderMenuItems()
          ) : (
            <Typography
              sx={{
                color: theme.palette.text.secondary,
                fontSize: 14,
                pl: '4px',
              }}
            >
              No items
            </Typography>
          )}
          {isEditable && onEdit && (
            <>
              {allOptions.length > 0 && (
                <Divider
                  sx={{
                    margin: '4px 0',
                    color: alpha(theme.palette.primary.main, 0.2),
                  }}
                />
              )}
              <MenuItem
                onClick={() => {
                  handleClose();
                  onEdit();
                }}
                sx={{
                  padding: '8px',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.2),
                  },
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%',
                }}
              >
                <EditIcon
                  sx={{
                    fontSize: 16,
                    color: theme.palette.primary.main,
                  }}
                />
                <ListItemText
                  primary={editLabel ?? 'Edit'}
                  primaryTypographyProps={{
                    fontSize: '14px',
                    color: theme.palette.primary.main,
                  }}
                />
              </MenuItem>
            </>
          )}
        </Select>
      )}
    </FormControl>
  );
};

export default OptionsDropdownSelector;
